import { createProgressiveProvider } from "./ProgressiveProvider";
import { createNativeHlsProvider } from "./NativeHlsProvider";
import { createHlsJsProvider } from "./HlsJsProvider";
import { createYouTubeEmbedProvider } from "./YouTubeEmbedProvider";
import { createVimeoEmbedProvider } from "./VimeoEmbedProvider";
import { detectMediaType, MediaType } from "../Core/MediaTypes";
import { videoSurfaceKind } from "../Providers/Playback/PlaybackProviderContract";
import { withCapabilities } from "../Providers/Playback/ProviderCapabilities";

function withVideoSurface(provider) {
  if (provider && typeof provider.getSurfaceKind !== "function") {
    provider.getSurfaceKind = videoSurfaceKind;
  }
  return withCapabilities(provider);
}

/**
 * Selects the best PlaybackProvider / StreamingProvider for a source.
 * Order: YouTube embed → Vimeo embed → native HLS → hls.js → progressive.
 *
 * Host code must not branch on provider identity — only on registry.select()
 * and engine.getCapabilities() / hasCapability().
 */
export function createStreamingProviderRegistry(options) {
  options = options || {};
  var enableEmbeds =
    options.enableEmbedProviders !== false &&
    String(
      (typeof process !== "undefined" &&
        process.env &&
        process.env.REACT_APP_DISABLE_EMBED_PROVIDERS) ||
        ""
    ) !== "1";

  var providers = [];
  if (enableEmbeds) {
    providers.push(withCapabilities(createYouTubeEmbedProvider()));
    providers.push(withCapabilities(createVimeoEmbedProvider()));
  }
  providers.push(
    withVideoSurface(createNativeHlsProvider()),
    withVideoSurface(createHlsJsProvider(options.hlsJs || {})),
    withVideoSurface(createProgressiveProvider())
  );

  if (Array.isArray(options.extraProviders)) {
    providers = options.extraProviders
      .map(withCapabilities)
      .concat(providers);
  }

  /**
   * @param {object} source
   * @returns {object|null}
   */
  function select(source) {
    if (!source || !source.src) {
      return null;
    }
    var normalized = Object.assign({}, source, {
      type: source.type || detectMediaType(source.src),
    });
    for (var i = 0; i < providers.length; i++) {
      if (providers[i].supports(normalized)) {
        return providers[i];
      }
    }
    // Last resort: progressive (may fail for unsupported URLs)
    return providers[providers.length - 1];
  }

  function list() {
    return providers.map(function (p) {
      return p.getName();
    });
  }

  return {
    select: select,
    list: list,
    providers: providers,
  };
}

/**
 * Convenience: resolve provider name that would handle a URL.
 * @param {string} url
 * @returns {string}
 */
export function predictProviderName(url) {
  var registry = createStreamingProviderRegistry();
  var provider = registry.select({ src: url, type: detectMediaType(url) });
  return provider ? provider.getName() : "none";
}

export { MediaType };
export default createStreamingProviderRegistry;
